import { RadicalPicker } from '@/components/inputs/RadicalPicker/RadicalPicker';
import { ResultDisplayWriting } from '@/components/results/ResultDisplayWriting';
import { capture } from '@/handlers/error';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { useComponentEditor } from '@/providers/editor/EditorProvider';
import { Coordinate, isSlideComponentRadicalWriting, PropsWithPreview, Question, Size, SlideComponent } from '@/types/slide';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { Box, Stack } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { ComponentLabel } from './ComponentLabel';
import { ContextualBar } from './ContextualBar';
import { useRightClickMenu } from './hooks/useRightClickMenu';
import { useSlideComponent } from './hooks/useSlideComponent';
import { FrontendIcon } from './icons/FrontendIcon';
import { RightClickMenu } from './RightClickMenu';
import { SlideRef } from './Slide';

interface Props {
    ref: React.RefObject<HTMLDivElement | null>;
    init: {
        model: SlideComponent;
        focus?: boolean;
    };
    slideRef: React.RefObject<SlideRef | null>;
    question?: Question;
}

export const SlideComponentRadicalWriting: React.FC<PropsWithPreview<Props>> = ({ ref, init, preview, slideRef, question }) => {
    // provider
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { deleteComponent, updateComponent, bringToFront, bringToBack, copyComponent, pasteComponent, undo, redo } = useComponentEditor();
    const { rightClickMenu, handleRightClickMenu, rightClickMenuClose } = useRightClickMenu();

    // refs
    const radicalPickerRef = useRef<HTMLDivElement | null>(null);

    // ui state
    const { isSelected } = useSlideComponent(ref, [], !!preview);
    const [model, setModel] = useState<SlideComponent>({ ...init.model });
    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);
    const [tSize, setTSize] = useState<Size>(model.size);
    const [tPos, setTPos] = useState<Coordinate>(model.pos);
    const [radicalPickerAnchorEl, setRadicalPickerAnchorEl] = useState<HTMLElement | null>(null);

    // init
    const isEditing = isFocus || isSelected;

    const onDeleteComponent = React.useCallback(
        async (id: string) => {
            try {
                await deleteComponent(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [deleteComponent, notifications],
    );

    const onUpdateComponent = React.useCallback(
        async (id: string, component: SlideComponent) => {
            try {
                await updateComponent(id, component);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [updateComponent, notifications],
    );

    const onBringToFrontHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToFront(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToFront, notifications],
    );

    const onBringToBackHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToBack(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToBack, notifications],
    );

    const debouncedUpdateComponent = useDebounce(onUpdateComponent, 500);

    useOutsideClick(ref, () => {
        if (preview) return;
        if (!ref) return;

        setIsFocus(false);
    });

    useEffect(() => {
        if (isFocus && !preview) {
            setRadicalPickerAnchorEl(ref.current);
        }
    }, [isFocus, preview, ref]);

    if (!isSlideComponentRadicalWriting(model)) {
        return null;
    }

    return (
        <Box
            onContextMenu={isEditing ? handleRightClickMenu : undefined}
            ref={ref}
            id={`radicalWriting-component-${model.id}`}
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                textAlign: 'left',
                width: tSize.width,
                height: tSize.height,
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
            onDoubleClick={() => {
                if (preview) return;
                setRadicalPickerAnchorEl(ref.current);
            }}
        >
            <ResizeBorder
                minSize={{ width: 200, height: 200 }}
                size={tSize}
                pos={tPos}
                preview={preview}
                isSelected={isEditing}
                onResize={(size, pos) => {
                    setTSize(size);
                    setTPos(pos);
                    const updateModel = { ...model, size, pos };
                    setModel({ ...updateModel });
                    debouncedUpdateComponent?.(model.id, { ...updateModel });
                }}
                aspectRatio={1}
                extraBoundary={{ right: 72, bottom: 0 }}
                slideRef={slideRef}
            >
                {!preview && (
                    <ComponentLabel
                        variant='caption'
                        sx={{
                            ...(!isEditing && {
                                opacity: 0,
                                transition: 'opacity 0.2s ease',
                                [`#radicalWriting-component-${model.id}:hover &`]: {
                                    opacity: 1,
                                },
                            }),
                        }}
                        noWrap
                    >
                        {t('slide.components.radicalWriting.title')}
                    </ComponentLabel>
                )}
                <Box
                    sx={{
                        width: tSize.width,
                        height: tSize.height,
                        backgroundColor: 'white',
                        backgroundImage: 'url(/asset/images/canvasWriting.svg)',
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                    }}
                >
                    {!model.content.character ? (
                        <QuestionMarkIcon
                            sx={{
                                width: 80,
                                height: 80,
                                color: (theme) => (theme.vars || theme).palette.grey[600],
                            }}
                        />
                    ) : (
                        <>
                            <Box
                                component='img'
                                alt='image'
                                src={`/asset/radicalDeformations/svg/${model.content.code}_${model.content.character}/${model.content.character}.svg`}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    filter: 'invert(85%) sepia(21%) saturate(213%) hue-rotate(334deg) brightness(108%) contrast(90%) opacity(80%)',
                                }}
                            />
                            {question && (
                                <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }}>
                                    <ResultDisplayWriting type='question' question={question} showReference={false} viewOnly={false} />
                                </Box>
                            )}
                        </>
                    )}
                </Box>
                <Stack sx={{ gap: 1, position: 'absolute', top: 0, right: 0 }}>
                    <FrontendIcon>
                        <DeleteIcon sx={{ width: 32, height: 32, color: (theme) => (theme.vars || theme).palette.grey[300] }} />
                    </FrontendIcon>
                </Stack>
                {!preview && isEditing && (
                    <ContextualBar
                        startButtons={[
                            {
                                icon: <EditIcon />,
                                tooltip: t('common.edit'),
                                onClick: () => {
                                    setRadicalPickerAnchorEl(ref.current);
                                },
                            },
                        ]}
                        endButtons={[
                            {
                                icon: <DeleteIcon />,
                                tooltip: t('common.remove'),
                                onClick: () => {
                                    onDeleteComponent?.(model.id);
                                },
                            },
                        ]}
                    />
                )}
                {!model.content?.character && <Box sx={{ color: 'error.main', mt: 1 }}>{t('slide.components.radicalWriting.warningMessage')}</Box>}

                <RadicalPicker
                    title={t('slide.components.radicalWriting.radicalSearch')}
                    anchorEl={radicalPickerAnchorEl}
                    slotProps={{
                        paper: {
                            ref: radicalPickerRef,
                        },
                    }}
                    onClose={() => {
                        setRadicalPickerAnchorEl(null);
                    }}
                    onUpdate={(settings) => {
                        const updatedContent = {
                            ...model.content,
                            code: settings?.code,
                            locale: settings?.locale,
                            character: settings?.character,
                        };

                        const updateModel = { ...model, content: updatedContent };

                        setModel(updateModel);
                        onUpdateComponent?.(model.id, updateModel);
                    }}
                />
            </ResizeBorder>
            <RightClickMenu
                componentId={model.id}
                contextMenu={rightClickMenu}
                handleClose={rightClickMenuClose}
                bringToFront={onBringToFrontHandler}
                bringToBack={onBringToBackHandler}
                copyComponent={copyComponent}
                pasteComponent={pasteComponent}
                undo={undo}
                redo={redo}
            />
        </Box>
    );
};
